import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Share2, Copy, MessageCircle, Mail, QrCode, ExternalLink, Sparkles, UserPlus, Users, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * ShareDialog — rich share popover with:
 *   • OG-tagged shareable URL (auto-unfurls on WhatsApp/Slack/Twitter/iMessage)
 *   • Direct review link (legacy)
 *   • One-tap WhatsApp deep link with pre-filled message
 *   • Email share
 *   • QR code (PNG download)
 *   • Per-link embed snippet
 *
 * The message template is configurable in Settings → Share message template.
 */
export default function ShareDialog({ open, onOpenChange, versionId, itemId }) {
  const [links, setLinks] = useState([]);
  const [mode, setMode] = useState("approve");
  const [password, setPassword] = useState("");
  const [expDays, setExpDays] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeToken, setActiveToken] = useState(null);
  const [activeMsg, setActiveMsg] = useState(null);  // { share_url, direct_url, message, whatsapp_url, mailto_url }

  const load = async () => {
    const { data } = await api.get(`/review-items/${itemId}/review-links`);
    setLinks(data);
    // Auto-select the most recent link to preview its share message
    if (data.length > 0 && !activeToken) setActiveToken(data[0].token);
  };

  useEffect(() => { if (open) load(); }, [open]);

  useEffect(() => {
    if (!activeToken) { setActiveMsg(null); return; }
    api.get(`/share/${activeToken}/message`).then((r) => setActiveMsg(r.data)).catch(() => setActiveMsg(null));
  }, [activeToken]);

  const createLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/versions/${versionId}/review-links`, {
        mode,
        password: password || null,
        expires_in_days: expDays ? Number(expDays) : null,
        require_email: true,
      });
      setPassword(""); setExpDays("");
      await load();
      setActiveToken(data.token);
      toast.success("Link created — share dialog updated");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create link"));
    } finally { setBusy(false); }
  };

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const qrUrl = activeToken ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/share/${activeToken}/qr.png` : null;

  // --- Per-link reviewer invites ---
  const [invites, setInvites] = useState([]);
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invSendEmail, setInvSendEmail] = useState(true);
  const [invBusy, setInvBusy] = useState(false);
  const activeLink = links.find((l) => l.token === activeToken);

  const loadInvites = async () => {
    if (!activeLink) { setInvites([]); return; }
    try {
      const { data } = await api.get(`/review-links/${activeLink.id}/invites`);
      setInvites(data);
    } catch { setInvites([]); }
  };
  useEffect(() => { loadInvites(); }, [activeLink?.id]);

  const sendInvite = async () => {
    if (!activeLink) return toast.error("Select a link first");
    if (!invName.trim() || !invEmail.trim()) return toast.error("Name and email required");
    setInvBusy(true);
    try {
      const { data } = await api.post(`/review-links/${activeLink.id}/invites`, {
        name: invName.trim(), email: invEmail.trim(), send_email: invSendEmail,
      });
      setInvName(""); setInvEmail("");
      await loadInvites();
      // Auto-copy the personalized URL
      const personalized = `${window.location.origin}/r/${activeLink.token}?i=${data.id}`;
      navigator.clipboard.writeText(personalized);
      toast.success(
        data.email_sent
          ? `Invite emailed to ${data.name} — link also copied`
          : `Invite created for ${data.name} — link copied`
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't create invite"));
    } finally { setInvBusy(false); }
  };

  const deleteInvite = async (id) => {
    try {
      await api.delete(`/reviewer-invites/${id}`);
      await loadInvites();
      toast.success("Invite removed");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to remove invite"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none h-8" data-testid="share-link-btn">
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share for review</DialogTitle>
          <DialogDescription>
            Smart links auto-unfurl on WhatsApp/Slack with a thumbnail preview. Pre-filled review messages too.
          </DialogDescription>
        </DialogHeader>

        {/* Create link */}
        <div className="border border-border p-3 space-y-3" data-testid="share-create-section">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9" data-testid="share-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View only</SelectItem>
                  <SelectItem value="comment">Comment</SelectItem>
                  <SelectItem value="approve">Comment + Approve</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Password (opt.)</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="—" className="h-9" data-testid="share-password-input" />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Expires in (days)</Label>
              <Input type="number" value={expDays} onChange={(e) => setExpDays(e.target.value)} placeholder="∞" className="h-9" data-testid="share-expiry-input" />
            </div>
          </div>
          <Button onClick={createLink} disabled={busy} className="w-full rounded-none" data-testid="share-create-link-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Creating…" : "Create new link"}
          </Button>
        </div>

        {/* Existing links + active link share controls */}
        <div className="space-y-2">
          <div className="label-eyebrow">Existing links</div>
          {links.length === 0 && <div className="text-xs text-muted-foreground py-3">No links yet. Create one above.</div>}
          <div className="space-y-2 max-h-32 overflow-auto">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => setActiveToken(l.token)}
                className={`w-full text-left border p-2 text-xs flex items-center justify-between ${
                  activeToken === l.token ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                }`}
                data-testid={`share-link-row-${l.id}`}
              >
                <div className="min-w-0">
                  <div className="font-mono truncate">/r/{l.token.slice(0, 18)}…</div>
                  <div className="text-muted-foreground">
                    {l.mode} {l.expires_at ? `· expires ${new Date(l.expires_at).toLocaleDateString()}` : "· no expiry"}
                  </div>
                </div>
                {activeToken === l.token && <span className="chip chip-approved text-[9px]">Active</span>}
              </button>
            ))}
          </div>
        </div>

        {activeMsg && (
          <div className="border border-border p-3 space-y-3" data-testid="share-active-panel">
            <Tabs defaultValue="message">
              <TabsList className="rounded-none">
                <TabsTrigger value="message" className="rounded-none text-xs" data-testid="share-tab-message">Message</TabsTrigger>
                <TabsTrigger value="links" className="rounded-none text-xs" data-testid="share-tab-links">Links</TabsTrigger>
                <TabsTrigger value="invites" className="rounded-none text-xs" data-testid="share-tab-invites"><UserPlus className="w-3 h-3 mr-1" />Invites</TabsTrigger>
                <TabsTrigger value="qr" className="rounded-none text-xs" data-testid="share-tab-qr">QR code</TabsTrigger>
                <TabsTrigger value="embed" className="rounded-none text-xs" data-testid="share-tab-embed">Embed</TabsTrigger>
              </TabsList>

              <TabsContent value="message" className="mt-3 space-y-3">
                <div className="border border-border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-auto" data-testid="share-message-preview">
                  {activeMsg.message}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Button onClick={() => copy(activeMsg.message, "Message copied")} variant="outline" className="rounded-none" data-testid="share-copy-message-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                  </Button>
                  <a href={activeMsg.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn">
                    <Button variant="outline" className="rounded-none w-full" style={{ borderColor: "#25D366", color: "#25D366" }}>
                      <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                    </Button>
                  </a>
                  <a href={activeMsg.mailto_url} data-testid="share-email-btn">
                    <Button variant="outline" className="rounded-none w-full">
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
                    </Button>
                  </a>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Edit the template in <strong>Settings → Share message template</strong>.
                </p>
              </TabsContent>

              <TabsContent value="links" className="mt-3 space-y-3">
                <div>
                  <Label className="label-eyebrow mb-1 inline-block">Smart preview link <span className="text-[9px] text-muted-foreground">(auto-unfurls in chats)</span></Label>
                  <div className="flex gap-1">
                    <Input readOnly value={activeMsg.share_url} className="h-9 font-mono text-xs" data-testid="share-url-input" />
                    <Button onClick={() => copy(activeMsg.share_url, "Smart link copied")} variant="outline" className="rounded-none h-9 shrink-0" data-testid="share-copy-smart-btn">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow mb-1 inline-block">Direct review link <span className="text-[9px] text-muted-foreground">(no preview unfurl)</span></Label>
                  <div className="flex gap-1">
                    <Input readOnly value={activeMsg.direct_url} className="h-9 font-mono text-xs" data-testid="share-direct-input" />
                    <Button onClick={() => copy(activeMsg.direct_url, "Direct link copied")} variant="outline" className="rounded-none h-9 shrink-0" data-testid="share-copy-direct-btn">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <a href={activeMsg.direct_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="rounded-none h-9 shrink-0" data-testid="share-open-direct-btn">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="invites" className="mt-3 space-y-3" data-testid="share-invites-panel">
                <p className="text-[11px] text-muted-foreground">
                  Personalized links pre-fill the reviewer&apos;s name + email and track who opened, commented, or approved.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={invName} onChange={(e) => setInvName(e.target.value)} placeholder="Reviewer name" className="h-9" data-testid="invite-name-input" />
                  <Input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="reviewer@email.com" className="h-9" data-testid="invite-email-input" />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="invite-send-email-toggle">
                  <input type="checkbox" checked={invSendEmail} onChange={(e) => setInvSendEmail(e.target.checked)} className="cursor-pointer" />
                  <Mail className="w-3.5 h-3.5" /> Email this invite via Resend (uses the share message template)
                </label>
                <Button onClick={sendInvite} disabled={invBusy} className="w-full rounded-none" data-testid="invite-send-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {invBusy ? "Creating…" : (invSendEmail ? "Create + email + copy link" : "Create + copy link")}
                </Button>
                <div className="space-y-1.5 max-h-48 overflow-auto">
                  {invites.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">No personalized invites yet.</div>}
                  {invites.map((iv) => {
                    const purl = `${window.location.origin}/r/${activeToken}?i=${iv.id}`;
                    return (
                      <div key={iv.id} className="border border-border p-2 text-xs" data-testid={`invite-row-${iv.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{iv.name} <span className="text-muted-foreground font-normal">· {iv.email}</span></div>
                            <div className="font-mono text-[10px] text-muted-foreground truncate">{purl}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => { navigator.clipboard.writeText(purl); toast.success("Link copied"); }} className="text-primary hover:underline inline-flex items-center" data-testid={`invite-copy-${iv.id}`}>
                              <Copy className="w-3 h-3" />
                            </button>
                            <button onClick={() => deleteInvite(iv.id)} className="text-destructive hover:underline inline-flex items-center" data-testid={`invite-delete-${iv.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="qr" className="mt-3 space-y-3 text-center" data-testid="share-qr-tab-content">
                {qrUrl && (
                  <div>
                    <img src={qrUrl} alt="Share QR code" className="mx-auto bg-white border border-border" width={220} height={220} data-testid="share-qr-img" />
                    <p className="text-[10px] text-muted-foreground mt-2">
                      Scan to open the review. Great for printed proofs or in-meeting handoffs.
                    </p>
                    <a href={qrUrl} download={`review-qr.png`} className="inline-block mt-2" data-testid="share-qr-download-btn">
                      <Button variant="outline" size="sm" className="rounded-none"><QrCode className="w-3.5 h-3.5 mr-1.5" /> Download PNG</Button>
                    </a>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="embed" className="mt-3 space-y-3">
                <Label className="label-eyebrow mb-1 inline-block">Iframe snippet</Label>
                <div className="border border-border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap" data-testid="share-embed-snippet">
                  {`<iframe src="${window.location.origin}/embed/r/${activeToken}" width="900" height="640" frameborder="0" allow="clipboard-write" title="Review"></iframe>`}
                </div>
                <Button onClick={() => copy(
                  `<iframe src="${window.location.origin}/embed/r/${activeToken}" width="900" height="640" frameborder="0" allow="clipboard-write" title="Review"></iframe>`,
                  "Embed snippet copied"
                )} variant="outline" className="rounded-none" data-testid={`share-embed-${activeToken}`}>
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy embed snippet
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
